import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../utils/rbac';
import Forbidden from '../common/Forbidden';

interface RequirePermissionProps {
  permissions: string | string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * RequirePermission Component
 *
 * Ensures the authenticated user has the specified permission(s) before rendering children.
 * Uses the RBAC system for permission checking.
 *
 * @param {string|string[]} permissions - Required permission(s)
 * @param {boolean} requireAll - Whether all permissions are required (default: false - any permission)
 * @param {React.ReactNode} fallback - Custom fallback component (default: Forbidden)
 * @param {React.ReactNode} children - Children to render if permission granted
 */
const RequirePermission: React.FC<RequirePermissionProps> = ({
  permissions,
  requireAll = false,
  fallback = <Forbidden />,
  children,
}) => {
  const { isAuthenticated, loading, user } = useAuth() as any; // Cast as any for now until AuthContext is fully typed
  const location = useLocation();
  const rbac = useRBAC(user);

  // Show loading state while auth is initializing
  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
        <div className='sm:mx-auto sm:w-full sm:max-w-md'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto'></div>
            <p className='mt-4 text-sm text-gray-600'>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  // Check permissions using RBAC system
  const requiredPerms = Array.isArray(permissions)
    ? permissions
    : [permissions];
  const hasAccess = requireAll
    ? rbac.hasAllPermissions(requiredPerms)
    : rbac.hasAnyPermission(requiredPerms);

  // Render fallback if no permission
  if (!hasAccess) {
    return fallback as React.ReactElement;
  }

  // Render children if permission granted
  return <>{children}</>;
};

export { RequirePermission };
export default RequirePermission;
